/**
 * WgChipsInput — chip + type-ahead WG picker.
 *
 * Used wherever a multi-WG list is being managed (tool restrictions,
 * server-bulk apply, WG drawer access grants). Replaces the
 * checkbox-popover pattern that breaks at scale: with this component
 * you only ever look at the autocomplete dropdown matching what you
 * typed, so 5 WGs and 5000 WGs feel identical.
 *
 * UX:
 *   - Existing entries render as removable chips inline.
 *   - Single text input on the right; typing filters the dropdown
 *     of remaining (un-selected) WGs.
 *   - Enter or click adds a chip; the dropdown closes when input is
 *     empty or no matches remain.
 *   - Backspace on an empty input removes the last chip — common in
 *     this pattern (Gmail, GitHub, Slack).
 *   - Each onChange is fired immediately on chip add/remove so the
 *     parent can save optimistically (no Save button needed).
 */
import { useEffect, useRef, useState, type JSX } from 'react';
import { X } from 'lucide-react';

export interface WgChipsInputProps {
  readonly value: readonly string[];
  readonly availableWgs: readonly string[];
  readonly onChange: (next: string[]) => void;
  readonly disabled?: boolean;
  readonly placeholder?: string;
  /** Hide the type-ahead input entirely — read-only mode. */
  readonly readOnly?: boolean;
}

const MAX_DROPDOWN = 8;

export function WgChipsInput({
  value,
  availableWgs,
  onChange,
  disabled,
  placeholder = 'type to add a WG…',
  readOnly,
}: WgChipsInputProps): JSX.Element {
  const [filter, setFilter] = useState('');
  const [highlighted, setHighlighted] = useState(0);
  const [open, setOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  // Close dropdown on outside click.
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent): void => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  const q = filter.trim().toLowerCase();
  const remaining = availableWgs.filter((id) => !value.includes(id));
  const filtered = q ? remaining.filter((id) => id.toLowerCase().includes(q)) : remaining;
  const dropdown = filtered.slice(0, MAX_DROPDOWN);

  const add = (id: string): void => {
    if (disabled || value.includes(id)) return;
    onChange([...value, id]);
    setFilter('');
    setHighlighted(0);
    setOpen(false);
    inputRef.current?.focus();
  };
  const remove = (id: string): void => {
    if (disabled) return;
    onChange(value.filter((x) => x !== id));
  };

  return (
    <div
      ref={containerRef}
      style={{
        position: 'relative',
        display: 'flex',
        flexWrap: 'wrap',
        gap: 4,
        alignItems: 'center',
        padding: '4px 6px',
        border: '1px solid var(--line)',
        borderRadius: 8,
        background: disabled ? 'rgba(0,0,0,0.04)' : 'transparent',
        minHeight: 34,
        cursor: readOnly ? 'default' : 'text',
      }}
      onClick={() => {
        if (!readOnly && !disabled) inputRef.current?.focus();
      }}
    >
      {value.length === 0 && !readOnly && (
        <span className="subtle" style={{ fontSize: 12 }}>
          (none — open to all WGs)
        </span>
      )}
      {value.map((id) => (
        <span
          key={id}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
            padding: '2px 4px 2px 8px',
            borderRadius: 999,
            background: 'rgba(95,178,122,0.15)',
            border: '1px solid rgba(95,178,122,0.45)',
            color: '#1f5a37',
            fontSize: 12,
          }}
        >
          <code style={{ fontSize: 11, color: 'inherit' }}>{id}</code>
          {!readOnly && (
            <button
              type="button"
              aria-label={`Remove ${id}`}
              onClick={(e) => {
                e.stopPropagation();
                remove(id);
              }}
              disabled={disabled}
              style={{
                background: 'transparent',
                border: 'none',
                cursor: disabled ? 'not-allowed' : 'pointer',
                color: 'inherit',
                padding: 0,
                display: 'inline-flex',
                alignItems: 'center',
              }}
            >
              <X size={11} />
            </button>
          )}
        </span>
      ))}
      {!readOnly && (
        <input
          ref={inputRef}
          type="text"
          value={filter}
          disabled={disabled}
          placeholder={value.length === 0 ? placeholder : ''}
          onFocus={() => setOpen(true)}
          onChange={(e) => {
            setFilter(e.target.value);
            setHighlighted(0);
            setOpen(true);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Backspace' && filter === '' && value.length > 0) {
              e.preventDefault();
              remove(value[value.length - 1]!);
              return;
            }
            if (!open && e.key !== 'Escape') setOpen(true);
            if (e.key === 'Escape') {
              setOpen(false);
              return;
            }
            if (e.key === 'ArrowDown') {
              e.preventDefault();
              setHighlighted((h) => Math.min(h + 1, dropdown.length - 1));
              return;
            }
            if (e.key === 'ArrowUp') {
              e.preventDefault();
              setHighlighted((h) => Math.max(h - 1, 0));
              return;
            }
            if (e.key === 'Enter' && dropdown[highlighted]) {
              e.preventDefault();
              add(dropdown[highlighted]!);
              return;
            }
          }}
          style={{
            flex: '1 1 120px',
            minWidth: 120,
            border: 'none',
            background: 'transparent',
            outline: 'none',
            fontSize: 13,
            padding: '4px 6px',
            color: 'inherit',
          }}
        />
      )}
      {open && !readOnly && dropdown.length > 0 && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            zIndex: 30,
            marginTop: 4,
            maxHeight: 280,
            overflow: 'auto',
            background: 'var(--background, white)',
            border: '1px solid var(--line-strong)',
            borderRadius: 8,
            boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
          }}
        >
          {dropdown.map((id, idx) => (
            <button
              key={id}
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => add(id)}
              onMouseEnter={() => setHighlighted(idx)}
              style={{
                display: 'block',
                width: '100%',
                textAlign: 'left',
                padding: '6px 10px',
                background: idx === highlighted ? 'rgba(140,21,21,0.08)' : 'transparent',
                border: 'none',
                cursor: 'pointer',
                fontSize: 13,
              }}
            >
              <code style={{ fontSize: 12 }}>{id}</code>
            </button>
          ))}
          {filtered.length > MAX_DROPDOWN && (
            <div
              className="subtle"
              style={{
                padding: '6px 10px',
                fontSize: 11,
                borderTop: '1px solid var(--line)',
              }}
            >
              {filtered.length - MAX_DROPDOWN} more — keep typing to narrow
            </div>
          )}
        </div>
      )}
    </div>
  );
}
